import { Box, IconButton, Link } from "@mui/material";
import { DataTableColumn } from "../../components/EmailDataTableComponent/DataTableComponent";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import ShowChartIcon from "@mui/icons-material/ShowChart";

const formatCurrency = (input: number | string): number | string => {
  const num = typeof input === "number" ? input : parseFloat(input);
  if (isNaN(num)) return "-";
  return "$" + Math.floor(num * 100) / 100;
};

const formatDate = (strdate: string) => {
  const date = new Date(strdate);
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  const formattedDate = `${day}.${month}.${year}`;
  return formattedDate;
};

function formatMileage(value: number | null) {
  return typeof value === "number" && !isNaN(value) ? Math.trunc(value) : "-";
}

export type DemoRate = {
  id: string;
  thread_id: string;
  message_id: string;
  date: string;
  tender: string;
  pod_rr: string;
  city: string;
  state: string;
  zip_code: string;
  cntr_gw: string;
  ow_fee_cntr: string;
  haz_ow: string;
  base_cost: string;
  base_cost_includes_fsc: boolean;
  base_cost_includes_daily_chassis: boolean;
  daily_chassis_rental: number;
  drayage: number;
  tri_axle_per_day: string;
  mil: number | null;
  validity?: string;
  included_charges_in_base_cost: string[]; // e.g., ["FSC"]
  accessorial_charges: string[];
  pricing_person: string;
  email: string;
  vendor_name: string;
  fuel_fixed_amount?: number;
  fsc?: number;
};

export const rateColumns = (
  onPriceClick: (row: DemoRate) => void
): DataTableColumn<DemoRate>[] => {
  return [
    //   { columnName: "Tender", fieldName: "tender" },
    {
      columnName: "Date",
      fieldName: "date",
      clipText: false,
      renderFn: (row) => formatDate(row.date),
    },
    { columnName: "POD / Ramp", fieldName: "pod_rr", flex: 2 },
    { columnName: "City", fieldName: "city" },
    { columnName: "State", fieldName: "state", flex: 0.6 },
    { columnName: "Zip", fieldName: "zip_code", clipText: false },
    {
      columnName: "Mileage",
      fieldName: "mil",
      flex: 0.5,
      renderFn: (row) => formatMileage(row.mil),
    },
    { columnName: "CNTR/GW", fieldName: "cntr_gw" },
    { columnName: "HAZ/OW", fieldName: "haz_ow" },
    {
      columnName: "Price",
      fieldName: "drayage",
      flex: 1.5,
      renderFn: (row) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Link
              component="button"
              onClick={(event) => {
                event.stopPropagation();
                onPriceClick(row);
              }}
              sx={{
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
                color: "primary.main",
              }}
            >
              {formatCurrency(row.drayage + 75)}
            </Link>
            <Tooltip title="Show trend">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onPriceClick(row);
                }}
                sx={{ p: 0.5 }}
              >
                <ShowChartIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      columnName: "Included Charges",
      fieldName: "included_charges_in_base_cost",
      clipText: true,
      flex: 2,
      renderFn: (row) => {
        const includedCharges = [...(row.included_charges_in_base_cost || [])];
        const containsFuel = includedCharges.some((item) =>
          /fsc|fuel/i.test(item)
        );
        const containsChassis = includedCharges.some((item) =>
          /chassis/i.test(item)
        );
        if ((row.fuel_fixed_amount || row.fsc) && !containsFuel) {
          includedCharges.push("FSC");
        }
        if (row.daily_chassis_rental && !containsChassis) {
          includedCharges.push("Chassis x 2");
        }
        return includedCharges.join(" + ");
      },
    },
    {
      columnName: "Accessorial Charges",
      fieldName: "accessorial_charges",
      clipText: true,
      flex: 2,
      visibleByDefault: false,
    },
    {
      columnName: "Overweight Fee (Container)",
      fieldName: "ow_fee_cntr",
      visibleByDefault: false,
    },
    {
      columnName: "Tri-Axle Per Day ($)",
      fieldName: "tri_axle_per_day",
      visibleByDefault: false,
      renderFn: (row) =>
        row.tri_axle_per_day ? `$${row.tri_axle_per_day}` : "-",
    },
    { columnName: "Validity", fieldName: "validity", flex: 0.6 },
    { columnName: "Vendor Name", fieldName: "vendor_name", clipText: true },
    { columnName: "Email", fieldName: "email", clipText: true },
    //   { columnName: "Agreement", fieldName: "agreement" },
    //   { columnName: "Pricing Person", fieldName: "pricing_person" },
    //   { columnName: "FTL / LTL", fieldName: "ftl_ltl" },
  ];
};

export type DemoQuote = {
  id: string;
  won_status: "won" | "lost" | "pending";
  quote_id: string;
  thread_id: string;
  message_id: string;
  date: string;
  tender: string;
  pod_rr: string;
  city: string;
  state: string;
  zip_code: string;
  cntr_gw: string;
  ow_fee_cntr: string;
  haz_ow: string;
  base_cost: string;
  base_cost_includes_fsc: boolean;
  base_cost_includes_daily_chassis: boolean;
  daily_chassis_rental: number;
  drayage: number;
  tri_axle_per_day: string;
  mil: number | null;
  validity?: string;
  included_charges_in_base_cost: string[]; // e.g., ["FSC"]
  accessorial_charges: string[];
  pricing_person: string;
  email: string;
  vendor_name: string;
  fuel_fixed_amount?: number;
  winning_price?: number;
  fsc?: number;
};

const vendorToWizardManufacturer: { [id: string]: string } = {
  "FastFreight Inc.": "Nimbus Broom Co.",
  Haulstars: "Firebolt Ltd.",
  "Ship Happens": "Weasleys' Wizard Wheezes",
  "Road Rage Logistics": "Slug & Jiggers Potions Co.",
  "Toll Troll Transport": "Magical Tools Co.",
  "Heavy Metal Movers": "Blast-Ended Skrewt Forge",
  "ZoomBoom Express": "FlooTech Industries",
  "Lazy Loaders": "Spindlewarps Enchanted Goods",
};

export const quoteColumns = (): DataTableColumn<DemoQuote>[] => {
  return [
    {
      columnName: "Date",
      fieldName: "date",
      clipText: false,
      renderFn: (row) => formatDate(row.date),
    },
    { columnName: "Quote ID", fieldName: "quote_id", flex: 1 },
    {
      columnName: "Client",
      flex: 1.5,
      clipText: true,
      renderFn: (row) => vendorToWizardManufacturer[row.vendor_name],
    },
    {
      columnName: "Price",
      fieldName: "drayage",
      renderFn: (row) => formatCurrency(row.drayage + 75),
    },
    {
      columnName: "Winning Price",
      fieldName: "drayage",
      renderFn: (row) =>
        row.winning_price ? formatCurrency(row.winning_price) : "-",
    },
    {
      columnName: "Won/Lost",
      fieldName: "won_status",
      clipText: false,
      flex: 1,
      renderFn: (row) => {
        let color: "success" | "error" | "default" = "default";
        let label = row.won_status;

        if (row.won_status === "won") color = "success";
        else if (row.won_status === "lost") color = "error";

        return (
          <Chip
            label={label}
            color={color}
            sx={{ fontSize: 10 }}
            size="small"
          />
        );
      },
    },
    { columnName: "POD / Ramp", fieldName: "pod_rr", flex: 2 },
    { columnName: "City", fieldName: "city" },
    { columnName: "State", fieldName: "state", flex: 0.6 },
    { columnName: "Zip", fieldName: "zip_code", clipText: false },
    {
      columnName: "Mileage",
      fieldName: "mil",
      flex: 0.5,
      renderFn: (row) => formatMileage(row.mil),
    },
    // { columnName: "CNTR/GW", fieldName: "cntr_gw" },
    // { columnName: "HAZ/OW", fieldName: "haz_ow" },
    { columnName: "Quote Validity", fieldName: "validity", flex: 0.6 },
    //   { columnName: "Agreement", fieldName: "agreement" },
    //   { columnName: "Pricing Person", fieldName: "pricing_person" },
    //   { columnName: "FTL / LTL", fieldName: "ftl_ltl" },
  ];
};
